// Wat er op het huishouden komt als iemand één vast gegeven invult op de kaart
// 'Vaste gegevens'. Puur, zodat de regels zonder Firestore te toetsen zijn en de
// kaart alleen hoeft te tonen en weg te schrijven.
//
// EÉN VELD PER PATCH. Een handler voor één veld schrijft geen buurveld, ook niet
// als het handig lijkt ('12A' in Huisnummer → Toevoeging 'A'): dan overschrijft
// hij een toevoeging die er al stond. In plaats daarvan een melding.
// AI-CONTEXT: See docs/pitfalls.md#state-handler-overwrites-sibling-field
// AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T4)

import { HOUSEHOLD_FIELDS } from './householdFields'
import { looksLikeEmail } from './householdEmail'
import normalizeEmail from '../normalizeEmail'

/**
 * @typedef {object} FixedFieldPatchResult
 * @property {Record<string, string|number|null>} [patch] - Wat er op `Adresses` komt
 * @property {string} [error] - Waarom de invoer niet opgeslagen wordt, in gewone taal
 */

const POSTCODE = /^(\d{4})\s?([a-zA-Z]{2})$/

/**
 * @param {string} field - Sleutel uit HOUSEHOLD_FIELDS
 * @param {string} raw - Wat de gebruiker invulde of koos
 * @returns {FixedFieldPatchResult}
 */
const buildFixedFieldPatch = (field, raw) => {
    const def = HOUSEHOLD_FIELDS[field]
    if (!def) throw new Error(`buildFixedFieldPatch: onbekend veld '${field}'`)

    const text = String(raw ?? '').trim()
    const value = (v) => ({ patch: { [field]: v } })

    if (def.kind === 'number') {
        if (!text) return value(null)
        if (!/^\d+$/.test(text) || Number(text) <= 0) return { error: 'Vul een heel getal groter dan 0 in.' }
        return value(Number(text))
    }

    if (def.kind === 'enum') {
        if (!text) return value('')
        // De sleutel, of het label van een keuze: een keuzevraag in een intake
        // slaat het label op ('sociale huur'). Opgeslagen wordt altijd de sleutel.
        const key = text.toLowerCase()
        const option = def.options.find((o) => o.value === text || o.label.toLowerCase() === key)
        if (!option) return { error: 'Kies een waarde uit de lijst.' }
        return value(option.value)
    }

    if (def.kind === 'email') {
        const email = normalizeEmail(text)
        if (email && !looksLikeEmail(email)) return { error: 'Dit is geen geldig e-mailadres.' }
        return value(email)
    }

    if (field === 'Postcode' && text) {
        const match = text.match(POSTCODE)
        if (!match) return { error: 'Vul een postcode in zoals 1234 AB.' }
        return value(`${match[1]} ${match[2].toUpperCase()}`)
    }

    if (field === 'Huisnummer' && text && !/^\d+$/.test(text)) {
        return { error: 'Alleen cijfers. Een letter of toevoeging zet je bij Toevoeging.' }
    }

    return value(text)
}

export default buildFixedFieldPatch
